/** @flow */

import React from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { localize } from "redux-i18n";
import { ColumnsContainer, Button, Row, Col, GSGrid, SectionComponent, Loader } from "@components";
import { getDay } from "date-fns";
import type { State } from "@store/reducers";
import type {
  EmployeeAbsenceShiftPatternOptions,
  EmployeeAbsence,
} from "@modules/shared/reducer/employee-absences/types";

import type { EmployeeAbsenceShiftPattern } from "@modules/employee/reducer/employment-details/types";
import sharedCreators from "@modules/shared/actionCreator";
import serviceActionCreators from "@modules/service/actionCreator";
import { getRowColor } from "@modules/shared/utils/shiftPatternRowColor";
import {
  getAbsenceShiftPatternOptions,
  getAbsenceShiftPatternDays,
  getAbsenceShiftPatternModalOpen,
  getAbsenceDetails,
  getAbsenceShiftPattern,
  isCaptureBankHolidaysEnabled,
  selectBankHolidays,
} from "@modules/shared/selectors/employee-absences";
import { EditEmployeeShiftPatternModal } from "@modules/shared/components";
import { transformSecondsInHours, formatDate } from "@transformers/date";
import type { Settings } from "@modules/settings/types";
import { getSettings } from "@modules/settings/selectors";
import type { EffectiveUserPermissions } from "@modules/service/reducer/user-permissions/types";
import * as userPermissionsSelectors from "@modules/service/selectors/user-permissions";
import * as employeeAbsenceSelectors from "@modules/shared/selectors/employee-absences";

type OwnProps = $Exact<{
  id: string,
  clientId: number,
  employee: Object,
  employeeId: number,

  absenceId: number,
  allowEdit?: boolean,
  isReportingSection?: boolean,
}>;

type PropsFromState = $Exact<{
  effectiveUserPermissions: ?EffectiveUserPermissions,
  isLoading: boolean,
  shiftPatternOptions: ?EmployeeAbsenceShiftPatternOptions,
  shiftPatternDays: ?Array<Object>,
  isShiftPatternModalOpen: boolean,
  absence: EmployeeAbsence | null,
  absenceShiftPattern: Array<EmployeeAbsenceShiftPattern>,
  settings: Settings | null,
  bankHolidays: Array<string>,
  captureBankHolidays: boolean | null,
}>;

type PropsFromDispatch = $Exact<{
  getEffectiveUserPermissions: typeof serviceActionCreators.GetEffectiveUserPermissions,
  getShiftPattern: typeof sharedCreators.GetEmployeeAbsenceShiftPattern,
  getShiftPatternOptions: typeof sharedCreators.GetEmployeeAbsenceShiftPatternOptions,
  toggleShiftPatternModal: typeof sharedCreators.ToggleEmployeeAbsenceShiftPatternModal,
  updateShiftPattern: typeof sharedCreators.UpdateEmployeeAbsenceShiftPattern,

  getEmployeeAbsence: typeof sharedCreators.GetEmployeeAbsence,
  getEmployeeBankHolidays: typeof sharedCreators.GetEmployeeBankHolidaysOptions,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

type ComponentState = $Exact<{
  isSectionOpen: boolean,
}>;

const daysOfWeekTranslationMap = {
  "0": "SUN",
  "1": "MON",
  "2": "TUE",
  "3": "WED",
  "4": "THU",
  "5": "FRI",
  "6": "SAT",
};

export class AbsenceDetailsShiftPattern extends React.Component<Props, ComponentState> {
  state = {
    isSectionOpen: true,
  };

  componentDidMount() {
    this.props.getEffectiveUserPermissions({ clientId: this.props.clientId });

    this.props.getShiftPattern({
      clientId: this.props.clientId,
      employeeId: this.props.employeeId,
      absenceId: this.props.absenceId,
    });

    this.props.getShiftPatternOptions({
      clientId: this.props.clientId,
      employeeId: this.props.employeeId,
    });

    this.props.getEmployeeAbsence({
      clientId: this.props.clientId,
      employeeId: this.props.employeeId,
      absenceId: this.props.absenceId,
      isReportingRequest: this.props.isReportingSection,
    });

    this.props.getEmployeeBankHolidays({
      clientId: this.props.clientId,
      employeeId: this.props.employeeId,
    });
  }

  showShiftPatternModal = (e: SyntheticEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    this.props.toggleShiftPatternModal(true);
  };

  closeShiftPatternModal = () => {
    this.props.toggleShiftPatternModal(false);
  };

  handleUpdateTimeLostPattern = (formData: any) => {
    this.props.updateShiftPattern({
      clientId: this.props.clientId,
      employeeId: this.props.employeeId,
      absenceId: this.props.absence.id,
      formData,
    });
    this.props.toggleShiftPatternModal(false);
  };

  toggleSection = () => {
    this.setState({
      isSectionOpen: !this.state.isSectionOpen,
    });
  };

  calculateDaysLost = (shiftPattern: EmployeeAbsenceShiftPattern) => {
    const result = (shiftPattern.timeLostInSeconds || 0) / (shiftPattern.workingTimeInSeconds || 1);
    return Math.round(result * 100) / 100;
  };

  get columns() {
    return [
      {
        name: this.props.t("DATE"),
        key: "recordedDay",
        align: "left",
        width: 150,
        renderCell: ({ row }: { row: EmployeeAbsenceShiftPattern }) => formatDate(row.recordedDay),
      },
      {
        name: this.props.t("DAY"),
        key: "day",
        width: 150,
        renderCell: ({ row }: { row: EmployeeAbsenceShiftPattern }) =>
          this.props.t(daysOfWeekTranslationMap[getDay(row.recordedDay)]),
        align: "left",
      },
      {
        name: this.props.t("DAY_TYPE"),
        renderCell: ({ row }: { row: EmployeeAbsenceShiftPattern }) => row.dayType.name,
        key: "dayType",
        width: 150,
        align: "left",
      },
      {
        name: this.props.t("ROLE"),
        renderCell: ({ row }: { row: EmployeeAbsenceShiftPattern }) =>
          row?.employeePosition?.jobTitle,
        key: "role",
        align: "left",
      },
      {
        name: this.props.t("HOURS_DUE_TO_WORK"),
        renderCell: ({ row }: { row: EmployeeAbsenceShiftPattern }) =>
          transformSecondsInHours(row.workingTimeInSeconds),
        key: "workingTimeInSeconds",
      },
      {
        name: this.props.t("HOURS_LOST"),
        renderCell: ({ row }: { row: EmployeeAbsenceShiftPattern }) =>
          transformSecondsInHours(row.timeLostInSeconds),
        key: "timeLostInSeconds",
      },
      {
        name: this.props.t("DAYS_LOST"),
        key: "dayLost",
        renderCell: ({ row }: { row: EmployeeAbsenceShiftPattern }) => {
          return this.calculateDaysLost(row);
        },
      },
    ];
  }

  render() {
    return (
      <React.Fragment>
        <SectionComponent
          id={this.props.id}
          wrapperClassName="flex-column"
          title={<h5>{this.props.t("SHIFTS")}</h5>}
          rightLink={
            this.props.allowEdit ? (
              <Button materialIcon="edit" onClick={this.showShiftPatternModal}>
                {this.props.t("EDIT")}
              </Button>
            ) : null
          }
          collapsable
          t={this.props.t}
          isOpen={this.state.isSectionOpen}
          onTitleClick={this.toggleSection}
        >
          <div className="d-flex">
            {this.props.isLoading && <Loader inline />}
            <div className="d-flex flex-column w-20 pr-50px ">
              <div className="py-2 border-top-color-charcoal-light">
                <div className="fs-14 opacity-60">{this.props.t("WORKING_HOURS_LOST")}</div>
                <div className="fs-16">
                  {this.props.absenceShiftPattern.reduce((acc, shiftPattern) => {
                    const hoursLostValue = parseFloat(
                      transformSecondsInHours(shiftPattern.timeLostInSeconds),
                    );
                    return parseFloat((acc + hoursLostValue).toFixed(2));
                  }, 0)}
                </div>
              </div>

              <div className="py-2 border-top-color-charcoal-light">
                <div className="fs-14 opacity-60">{this.props.t("WORKING_DAYS_LOST")}</div>
                <div className="fs-16">
                  {parseFloat(
                    this.props.absenceShiftPattern
                      .reduce((acc, shiftPattern) => {
                        return acc + this.calculateDaysLost(shiftPattern);
                      }, 0)
                      .toFixed(2),
                  )}
                </div>
              </div>

              <div className="py-2 border-top-color-charcoal-light">
                <div className="fs-14 opacity-60">{this.props.t("CALENDAR_DAYS_LOST")}</div>
                <div className="fs-16">{(this.props.absence || {}).calendarDaysLost || 0}</div>
              </div>
            </div>

            <GSGrid
              noResultsText={this.props.t("NO_SHIFT_PATTERN_FOUND")}
              rows={this.props.absenceShiftPattern}
              rowClass={(row, idx) => getRowColor(this.props.bankHolidays)(row, idx, true)}
              columns={this.columns}
            />
          </div>
        </SectionComponent>

        {this.props.isShiftPatternModalOpen &&
          this.props.settings &&
          this.props.absence &&
          this.props.bankHolidays &&
          this.props.captureBankHolidays !== null &&
          this.props?.shiftPatternOptions?.employeePosition && (
            <EditEmployeeShiftPatternModal
              absence={{
                ...this.props.absence,
              }}
              employee={this.props.employee}
              bankHolidays={this.props.bankHolidays}
              captureBankHolidays={this.props.captureBankHolidays}
              timeLostShiftPattern={this.props.absenceShiftPattern}
              shiftPatternDays={this.props.shiftPatternDays}
              settings={this.props.settings}
              onSubmit={this.handleUpdateTimeLostPattern}
              onDismissHandler={this.closeShiftPatternModal}
              minimumAbsenceDuration={this.props.settings.serviceSettings.minimumAbsenceDuration}
            />
          )}
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state: State): PropsFromState => ({
  absence: getAbsenceDetails(state),
  absenceShiftPattern: getAbsenceShiftPattern(state),

  isLoading: employeeAbsenceSelectors.isLoading(state),

  bankHolidays: selectBankHolidays(state),
  captureBankHolidays: isCaptureBankHolidaysEnabled(state),

  shiftPatternOptions: getAbsenceShiftPatternOptions(state),
  shiftPatternDays: getAbsenceShiftPatternDays(state),

  isShiftPatternModalOpen: getAbsenceShiftPatternModalOpen(state),

  settings: getSettings(state),
  effectiveUserPermissions: userPermissionsSelectors.getEffectiveUserPermissions(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  getEffectiveUserPermissions: serviceActionCreators.GetEffectiveUserPermissions,
  getShiftPattern: sharedCreators.GetEmployeeAbsenceShiftPattern,
  getShiftPatternOptions: sharedCreators.GetEmployeeAbsenceShiftPatternOptions,
  toggleShiftPatternModal: sharedCreators.ToggleEmployeeAbsenceShiftPatternModal,
  updateShiftPattern: sharedCreators.UpdateEmployeeAbsenceShiftPattern,
  getEmployeeAbsence: sharedCreators.GetEmployeeAbsence,
  getEmployeeBankHolidays: sharedCreators.GetEmployeeBankHolidaysOptions,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(AbsenceDetailsShiftPattern);
